import type { RaRecord } from "react-admin";
import { isPersonalSabilType } from "@/constants";
import {
  isMuttavatteenSabilType,
  mapHouseholdMembersForApi,
  normalizeWhatsappNo,
  type SabilHouseholdMemberInput,
} from "../sabil-household-members";
import { resolveSabilEditItsNo } from "./resolve-sabil-edit-its-no";

function withAuditRemark(
  data: RaRecord,
  payload: Record<string, unknown>
): Record<string, unknown> {
  if (!data.auditRemark) {
    return payload;
  }
  return { ...payload, auditRemark: String(data.auditRemark).trim() };
}

/** Keep loaded name/address when ITS lookup wiped the form (directory miss). */
function coalesceEditText(formValue: unknown, previousValue: unknown): unknown {
  if (String(formValue ?? "").trim()) {
    return formValue;
  }
  if (String(previousValue ?? "").trim()) {
    return previousValue;
  }
  return formValue;
}

/**
 * Build the sabilData update payload.
 * `sabilType` is disabled on the edit form, so react-hook-form omits it from
 * submit values — fall back to `previousData.sabilType` from the loaded record.
 */
export function transformSabilDataEdit(
  data: RaRecord,
  options?: { previousData?: RaRecord }
): Record<string, unknown> {
  const sabilType = String(data.sabilType ?? options?.previousData?.sabilType ?? "");
  const itsNo = resolveSabilEditItsNo(data, sabilType);
  const name = coalesceEditText(data.name, options?.previousData?.name);
  const address = coalesceEditText(data.address, options?.previousData?.address);
  const additionalMembers = data.householdMembers as SabilHouseholdMemberInput[] | undefined;
  const householdMembers = isMuttavatteenSabilType(sabilType)
    ? (mapHouseholdMembersForApi(additionalMembers) ?? [])
    : null;
  const holderWhatsappNo = isMuttavatteenSabilType(sabilType)
    ? normalizeWhatsappNo(data.whatsappNo)
    : undefined;

  if (isPersonalSabilType(sabilType)) {
    return withAuditRemark(data, {
      id: data.id,
      itsNo,
      name,
      address,
      pan: data.pan,
      remarks: data.remarks,
      ...(holderWhatsappNo !== undefined ? { holderWhatsappNo } : {}),
      ...(householdMembers ? { householdMembers } : {}),
    });
  }

  return withAuditRemark(data, {
    id: data.id,
    itsNo,
    name,
    address,
    firmName: data.firmName,
    firmAddress: data.firmAddress,
    pan: data.pan,
    remarks: data.remarks,
  });
}
